/**
 * StatsOverviewPanel — own-profile right-rail card.
 *
 * Renders headline label/value pairs in a small grid plus a sparkline SVG
 * built from the spark number array (normalised polyline into a 100×28 viewBox).
 */
import styled, { useTheme } from 'styled-components';
import { Panel, SectionLabel } from '@/ui';
import type { User } from '@/core/models';

/* ── Styled helpers ─────────────────────────────────────────────────────────── */

const HeadlineGrid = styled.dl`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px 8px;
  margin: 0 0 14px;
  padding: 0;
`;

const StatCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const StatValue = styled.dd`
  margin: 0;
  font-family: ${({ theme }) => theme.font.display};
  font-size: ${({ theme }) => theme.fontSize.lg};
  font-weight: 700;
  color: ${({ theme }) => theme.color.textPrimary};
  line-height: 1.1;
`;

const StatLabel = styled.dt`
  margin: 0;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
`;

const SparkWrap = styled.div`
  margin-top: 4px;
`;

const EmptyState = styled.p`
  margin: 0;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textMuted};
`;

/* ── Sparkline ───────────────────────────────────────────────────────────────── */

const SPARK_W = 100;
const SPARK_H = 28;

function buildPoints(spark: number[]): string {
  if (spark.length < 2) return '';
  const min = Math.min(...spark);
  const max = Math.max(...spark);
  const range = max - min;
  return spark
    .map((v, i) => {
      const x = (i / (spark.length - 1)) * SPARK_W;
      // Guard divide-by-zero: if all values equal, render flat at mid-height.
      const y = range === 0 ? SPARK_H / 2 : SPARK_H - ((v - min) / range) * SPARK_H;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');
}

interface SparklineProps {
  spark: number[];
}

function Sparkline({ spark }: SparklineProps) {
  const theme = useTheme();
  const points = buildPoints(spark);
  if (!points) return null;

  return (
    <SparkWrap>
      <svg
        viewBox={`0 0 ${SPARK_W} ${SPARK_H}`}
        width={SPARK_W}
        height={SPARK_H}
        aria-hidden="true"
        style={{ display: 'block', overflow: 'visible' }}
      >
        <polyline
          points={points}
          fill="none"
          stroke={theme.color.accentCyan}
          strokeWidth="2"
          strokeLinejoin="round"
          strokeLinecap="round"
        />
      </svg>
    </SparkWrap>
  );
}

/* ── Component ──────────────────────────────────────────────────────────────── */

export interface StatsOverviewPanelProps {
  user: User;
}

export function StatsOverviewPanel({ user }: StatsOverviewPanelProps) {
  const overview = user.statsOverview;

  return (
    <Panel>
      <SectionLabel as="h2">Stats Overview</SectionLabel>

      {(!overview || (overview.headline.length === 0 && overview.spark.length < 2)) ? (
        <EmptyState>No stats available yet.</EmptyState>
      ) : (
        <>
          {overview.headline.length > 0 && (
            <HeadlineGrid>
              {overview.headline.map((item) => (
                <StatCell key={item.label}>
                  <StatValue>{item.value}</StatValue>
                  <StatLabel>{item.label}</StatLabel>
                </StatCell>
              ))}
            </HeadlineGrid>
          )}
          {overview.spark.length >= 2 && <Sparkline spark={overview.spark} />}
        </>
      )}
    </Panel>
  );
}
